import {
  DefaultInput,
  Game,
  GameWrapper,
  NetplayPlayer,
  type GameClass,
} from 'netplayjs';
import {
  PeerRollbackNetcode,
  type DesyncInfo,
} from '@/netplay/PeerRollbackNetcode';

const HUD_HEIGHT_PX = 148;
const MAX_PREDICTED_FRAMES = 10;
const PING_INTERVAL_MS = 100;

type PeerInputMessage = {
  type: 'input';
  frame: number;
  input: ReturnType<DefaultInput['serialize']>;
};

type PeerStateHashMessage = {
  type: 'state-hash';
  frame: number;
  hash: string;
};

type PeerDesyncMessage = {
  type: 'desync';
  reason: string;
  details: unknown;
};

type PeerPingReqMessage = {
  type: 'ping-req';
  sent_time: number;
};

type PeerPingRespMessage = {
  type: 'ping-resp';
  sent_time: number;
};

type PeerMessage =
  | PeerInputMessage
  | PeerStateHashMessage
  | PeerDesyncMessage
  | PeerPingReqMessage
  | PeerPingRespMessage;

interface PlayerHudRefs {
  title: HTMLDivElement;
  hpText: HTMLSpanElement;
  hpFill: HTMLDivElement;
  fuelText: HTMLSpanElement;
  fuelFill: HTMLDivElement;
  coinsText: HTMLSpanElement;
  coinsFill: HTMLDivElement;
}

function ratio(current: number, max: number): number {
  if (max <= 0) return 0;
  return Math.max(0, Math.min(1, current / max));
}

function hearts(current: number, max: number): string {
  const clampedCurrent = Math.max(0, Math.min(max, current));
  const full = '\u2665'.repeat(clampedCurrent);
  const empty = '\u2661'.repeat(Math.max(0, max - clampedCurrent));
  return `${full}${empty}`;
}

class PingEstimate {
  private avg = 0;
  private initialized = false;

  update(value: number): void {
    if (!Number.isFinite(value)) return;
    if (!this.initialized) {
      this.avg = value;
      this.initialized = true;
      return;
    }
    this.avg = this.avg * 0.8 + value * 0.2;
  }

  average(): number {
    return this.initialized ? this.avg : 0;
  }
}

type WrapperConnection = Parameters<GameWrapper['startHost']>[1];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parsePeerMessage(raw: unknown): PeerMessage | null {
  if (!isObject(raw) || typeof raw.type !== 'string') return null;

  if (raw.type === 'input') {
    if (typeof raw.frame !== 'number' || !isObject(raw.input)) return null;
    return { type: 'input', frame: raw.frame, input: raw.input as ReturnType<DefaultInput['serialize']> };
  }

  if (raw.type === 'state-hash') {
    if (typeof raw.frame !== 'number' || typeof raw.hash !== 'string') return null;
    return { type: 'state-hash', frame: raw.frame, hash: raw.hash };
  }

  if (raw.type === 'desync') {
    return {
      type: 'desync',
      reason: typeof raw.reason === 'string' ? raw.reason : 'unknown',
      details: raw.details,
    };
  }

  if (raw.type === 'ping-req' || raw.type === 'ping-resp') {
    if (typeof raw.sent_time !== 'number') return null;
    return { type: raw.type, sent_time: raw.sent_time };
  }

  return null;
}

export class MoonDuelRollbackWrapper extends GameWrapper {
  private readonly pingMeasure = new PingEstimate();
  private readonly pingValue: HTMLSpanElement;
  private readonly playerHud: Map<number, PlayerHudRefs>;
  private game?: Game;
  private netcode?: PeerRollbackNetcode<Game, DefaultInput>;
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  constructor(gameClass: GameClass) {
    super(gameClass);

    document.body.style.margin = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.background = '#060910';

    this.playerHud = new Map();
    const hudRoot = this.createHud();
    this.pingValue = hudRoot.querySelector('[data-hud="ping-value"]') as HTMLSpanElement;

    this.removePublicMatchmakingOption();
    this.enableInviteLinkCopyBehavior();
  }

  override calculateLayout(
    container: { width: number; height: number },
    canvas: { width: number; height: number }
  ): { width: number; height: number; left: number; top: number } {
    const gameplayHeight = Math.max(160, container.height - HUD_HEIGHT_PX);
    const layout = super.calculateLayout(
      { width: container.width, height: gameplayHeight },
      canvas
    );
    return { ...layout, top: layout.top + HUD_HEIGHT_PX };
  }

  override startHost(players: NetplayPlayer[], conn: WrapperConnection): void {
    this.startPeerRollback(players, conn, players[1]);
  }

  override startClient(players: NetplayPlayer[], conn: WrapperConnection): void {
    this.startPeerRollback(players, conn, players[0]);
  }

  private startPeerRollback(
    players: NetplayPlayer[],
    conn: WrapperConnection,
    remotePlayer: NetplayPlayer
  ): void {
    this.game = new this.gameClass(this.canvas, players);

    this.netcode = new PeerRollbackNetcode(
      this.game,
      players,
      this.getInitialInputs(players),
      MAX_PREDICTED_FRAMES,
      this.gameClass.timestep,
      () => this.inputReader.getInput(),
      (frame, input) => {
        conn.send({ type: 'input', frame, input: input.serialize() });
      },
      (frame, hash) => {
        conn.send({ type: 'state-hash', frame, hash });
      },
      (info) => {
        this.sendDesyncAndClose(conn, info);
      }
    );

    // The peer channel only accepts explicitly parsed message shapes.
    conn.on('data', (data: unknown) => {
      const message = parsePeerMessage(data);
      if (!message) return;

      if (message.type === 'input') {
        const input = new DefaultInput();
        input.deserialize(message.input);
        this.netcode?.onRemoteInput(message.frame, remotePlayer, input);
        return;
      }

      if (message.type === 'state-hash') {
        this.netcode?.onRemoteStateHash(message.frame, message.hash);
        return;
      }

      if (message.type === 'desync') {
        this.netcode?.onRemoteDesync();
        conn.close();
        return;
      }

      if (message.type === 'ping-req') {
        conn.send({ type: 'ping-resp', sent_time: message.sent_time });
        return;
      }

      if (message.type === 'ping-resp') {
        this.pingMeasure.update(Date.now() - message.sent_time);
      }
    });

    conn.onClose.on(() => {
      this.netcode?.stop();
      this.netcode = undefined;
      this.clearPingTimer();
    });

    this.setPingTimer(conn);
    this.startGameLoop();
  }

  private getInitialInputs(players: NetplayPlayer[]): Map<NetplayPlayer, DefaultInput> {
    const initialInputs = new Map<NetplayPlayer, DefaultInput>();
    for (const player of players) {
      initialInputs.set(player, new DefaultInput());
    }
    return initialInputs;
  }

  private setPingTimer(conn: WrapperConnection): void {
    this.clearPingTimer();
    this.pingInterval = setInterval(() => {
      conn.send({ type: 'ping-req', sent_time: Date.now() });
    }, PING_INTERVAL_MS);
  }

  private clearPingTimer(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private sendDesyncAndClose(conn: WrapperConnection, info: DesyncInfo): void {
    conn.send({
      type: 'desync',
      reason: info.reason,
      details: info.details ?? null,
    });
    conn.close();
  }

  private startGameLoop(): void {
    this.stats.style.display = 'none';
    this.netcode?.start();

    const animate = (): void => {
      this.game?.draw(this.canvas);
      this.updateHud();
      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }

  private createHud(): HTMLDivElement {
    const hud = document.createElement('div');
    hud.style.position = 'absolute';
    hud.style.left = '0';
    hud.style.top = '0';
    hud.style.right = '0';
    hud.style.height = `${HUD_HEIGHT_PX}px`;
    hud.style.display = 'grid';
    hud.style.gridTemplateColumns = '1fr auto 1fr';
    hud.style.alignItems = 'stretch';
    hud.style.gap = '12px';
    hud.style.padding = '12px 16px';
    hud.style.boxSizing = 'border-box';
    hud.style.background = 'linear-gradient(180deg, #0f1626 0%, #101b32 100%)';
    hud.style.borderBottom = '2px solid #2a3859';
    hud.style.fontFamily = '"Trebuchet MS", "Segoe UI", sans-serif';
    hud.style.color = '#edf3ff';
    hud.style.zIndex = '2';

    const p1 = this.createPlayerPanel('P1');
    const p2 = this.createPlayerPanel('P2');
    this.playerHud.set(0, p1.refs);
    this.playerHud.set(1, p2.refs);

    const center = document.createElement('div');
    center.style.display = 'flex';
    center.style.flexDirection = 'column';
    center.style.justifyContent = 'center';
    center.style.alignItems = 'center';
    center.style.gap = '6px';
    center.style.minWidth = '220px';

    const ping = document.createElement('div');
    ping.style.display = 'flex';
    ping.style.alignItems = 'center';
    ping.style.gap = '8px';
    ping.style.padding = '6px 12px';
    ping.style.border = '1px solid #4461a0';
    ping.style.borderRadius = '999px';
    ping.style.background = '#0d1a33';
    ping.style.fontSize = '15px';
    ping.innerHTML = '<span>Ping</span><span data-hud="ping-value" style="font-weight: 700;">-- ms</span>';

    center.appendChild(ping);
    center.appendChild(this.createHowToPlayPanel());

    hud.appendChild(p1.root);
    hud.appendChild(center);
    hud.appendChild(p2.root);

    document.body.appendChild(hud);
    return hud;
  }

  private createHowToPlayPanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.style.fontSize = '11px';
    panel.style.lineHeight = '1.25';
    panel.style.color = '#c7d7f5';
    panel.style.textAlign = 'center';
    panel.style.maxWidth = '360px';
    panel.style.padding = '4px 8px';

    panel.innerHTML = [
      '<div><strong>Win:</strong> KO opponent OR collect 3 cheese and touch the rocket.</div>',
      '<div><strong>Move:</strong> Arrow Keys / A D &nbsp; <strong>Jump:</strong> Space / W / Up</div>',
      '<div><strong>Fire:</strong> F / J &nbsp; <strong>Jetpack:</strong> Shift / K</div>',
    ].join('');

    return panel;
  }

  private createPlayerPanel(label: string): { root: HTMLDivElement; refs: PlayerHudRefs } {
    const root = document.createElement('div');
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.justifyContent = 'center';
    root.style.gap = '8px';
    root.style.padding = '8px 10px';
    root.style.background = '#0b1428';
    root.style.border = '1px solid #2a3e67';
    root.style.borderRadius = '10px';

    const title = document.createElement('div');
    title.style.fontSize = '13px';
    title.style.fontWeight = '700';
    title.textContent = label;

    const hpRow = this.createMetricRow('HP');
    const hpBar = this.createBar('#e84d70');
    hpRow.barWrap.appendChild(hpBar.fill);

    const fuelRow = this.createMetricRow('Fuel');
    const fuelBar = this.createBar('#67d6ff');
    fuelRow.barWrap.appendChild(fuelBar.fill);

    const coinsRow = this.createMetricRow('Cheese');
    const coinsBar = this.createBar('#f6cc4a');
    coinsRow.barWrap.appendChild(coinsBar.fill);

    root.appendChild(title);
    root.appendChild(hpRow.root);
    root.appendChild(fuelRow.root);
    root.appendChild(coinsRow.root);

    return {
      root,
      refs: {
        title,
        hpText: hpRow.value,
        hpFill: hpBar.fill,
        fuelText: fuelRow.value,
        fuelFill: fuelBar.fill,
        coinsText: coinsRow.value,
        coinsFill: coinsBar.fill,
      },
    };
  }

  private createMetricRow(label: string): {
    root: HTMLDivElement;
    value: HTMLSpanElement;
    barWrap: HTMLDivElement;
  } {
    const row = document.createElement('div');
    row.style.display = 'grid';
    row.style.gridTemplateColumns = '58px 1fr auto';
    row.style.alignItems = 'center';
    row.style.gap = '8px';
    row.style.fontSize = '12px';

    const name = document.createElement('span');
    name.style.color = '#b8caef';
    name.style.fontWeight = '700';
    name.textContent = label;

    const barWrap = document.createElement('div');
    barWrap.style.height = '8px';
    barWrap.style.borderRadius = '999px';
    barWrap.style.background = '#1f2b46';
    barWrap.style.overflow = 'hidden';

    const value = document.createElement('span');
    value.style.fontWeight = '700';
    value.textContent = '--';

    row.appendChild(name);
    row.appendChild(barWrap);
    row.appendChild(value);

    return { root: row, value, barWrap };
  }

  private createBar(color: string): { fill: HTMLDivElement } {
    const fill = document.createElement('div');
    fill.style.height = '100%';
    fill.style.width = '0%';
    fill.style.background = color;
    return { fill };
  }

  private updateHud(): void {
    const pingMs = Math.max(0, Math.round(this.pingMeasure.average()));
    this.pingValue.textContent = `${pingMs} ms`;

    // Debug snapshots are written by the game instance each tick.
    const debug = window.__moonDuelDebug?.instances?.filter(Boolean) ?? [];
    if (debug.length === 0) return;

    const snapshot = [...debug].sort((a, b) => (b?.frame ?? 0) - (a?.frame ?? 0))[0];
    if (!snapshot) return;

    const requiredCoins = snapshot.requiredCoinsToLaunch;

    for (const player of snapshot.players) {
      const refs = this.playerHud.get(player.id);
      if (!refs) continue;

      const isLocal = player.id === snapshot.localPlayerId;
      refs.title.textContent = isLocal ? `P${player.id + 1} (You)` : `P${player.id + 1}`;

      const hpDisplay = hearts(player.health, player.maxHealth);
      refs.hpText.textContent = `${hpDisplay} ${player.health}/${player.maxHealth}`;
      refs.hpFill.style.width = `${Math.round(ratio(player.health, player.maxHealth) * 100)}%`;

      refs.fuelText.textContent = `${player.jetpackFuel}/${player.maxJetpackFuel}`;
      refs.fuelFill.style.width = `${Math.round(ratio(player.jetpackFuel, player.maxJetpackFuel) * 100)}%`;

      refs.coinsText.textContent = `${player.coins}/${requiredCoins}`;
      refs.coinsFill.style.width = `${Math.round(ratio(player.coins, requiredCoins) * 100)}%`;
    }
  }

  private removePublicMatchmakingOption(): void {
    const prune = (): boolean => {
      let removed = false;

      const matchmakingButton = [...document.querySelectorAll('button')].find((button) =>
        (button.textContent ?? '').includes('Start Matchmaking')
      ) as HTMLButtonElement | undefined;

      if (!matchmakingButton) return false;

      const publicMatchColumn = matchmakingButton.parentElement as HTMLElement | null;
      const grid = publicMatchColumn?.parentElement as HTMLElement | null;
      publicMatchColumn?.remove();
      removed = true;

      if (grid) {
        for (const child of [...grid.children]) {
          const element = child as HTMLElement;
          if (element.style.width === '1px' || (element.textContent ?? '').includes('Public Match')) {
            element.remove();
          }
        }
        grid.style.gridTemplateColumns = '1fr';
        grid.style.gridColumnGap = '0';
      }

      return removed;
    };

    if (prune()) return;

    const observer = new MutationObserver(() => {
      if (prune()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 30_000);
  }

  private enableInviteLinkCopyBehavior(): void {
    const bindInviteLink = (): void => {
      const inviteLinks = [...document.querySelectorAll('a[href*="#room="]')] as HTMLAnchorElement[];
      for (const link of inviteLinks) {
        if (link.dataset.copyInviteBound === '1') continue;
        link.dataset.copyInviteBound = '1';
        link.removeAttribute('target');
        link.style.cursor = 'copy';
        link.style.textDecoration = 'underline';
        link.title = 'Click to copy invite URL';

        link.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const url = link.href;
          void this.copyText(url);
        });
      }
    };

    bindInviteLink();

    const observer = new MutationObserver(() => {
      bindInviteLink();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  private async copyText(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Fall back to a temporary textarea for browsers that block clipboard API.
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  }
}
